class CollectionGridLayout {
  constructor() {
    this.init();
  }

  init() {
    // Ejecutar cuando el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.applyProductFormats());
    } else {
      this.applyProductFormats();
    }

    // Re-evaluar cuando se carguen nuevos productos (infinite scroll)
    window.addEventListener('infiniteScroll:loaded', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se eliminen banners duplicados
    window.addEventListener('collection:layoutChanged', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se apliquen filtros
    document.addEventListener('facets:change', () => {
      // Usar setTimeout para esperar a que el DOM se actualice completamente
      setTimeout(() => {
        // Re-configurar el observer porque el DOM se reemplazó
        this.observeGridChanges();
        // Aplicar formatos
        this.applyProductFormats();
      }, 200);
    });

    // Observer para detectar cambios en data-columns
    this.observeGridChanges();

    // Re-evaluar cuando se redimensiona la ventana (móvil <-> desktop)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.applyProductFormats();
      }, 250);
    });
  }

  /**
   * Detecta items "huérfanos" que quedan solos en una fila.
   * Casos detectados (móvil y desktop - los formatos dobles aplican en ambos):
   * 1. Producto simple seguido de producto doble (mala configuración en backoffice)
   * 2. Producto simple + producto OOS + producto doble (OOS oculto deja hueco)
   * 3. Producto OOS + producto simple + producto doble (OOS a la izquierda)
   * 4. Último producto solo en su fila cuando no hay más productos por cargar
   *
   * @param {Array} allItems - Todos los items del grid (incluyendo OOS)
   * @param {Array} visibleItems - Solo los items visibles (sin OOS)
   * @param {boolean} isDesktop - Si estamos en desktop (>768px)
   */
  detectOrphanItems(allItems, visibleItems, isDesktop) {
    const orphanItems = new Set();

    // Función para determinar si un formato ocupa toda la fila (es "doble")
    // NOTA: Estos formatos usan grid-column: 1 / -1 tanto en móvil como en desktop
    const isDoubleFormat = (format) => {
      return format === 'panoramica_actual' ||
             format === 'panoramica_grande' ||
             format === 'vertical_grande';
    };

    // Función para obtener el formato original de un item (sin modificaciones previas)
    const getOriginalFormat = (item) => {
      return item.dataset.originalFormat || item.getAttribute('data-format');
    };

    // Una tarjeta de "Shop the look" ocupa la fila entera en 2 columnas sea cual
    // sea su formato, así que cuenta como doble aunque no tenga data-format.
    const isFullRowItem = (item) => {
      if (!item) return false;
      if (item.classList.contains('collection-look-item')) return true;
      return isDoubleFormat(getOriginalFormat(item));
    };

    // Casos 1, 2, 3: Aplican tanto en móvil como en desktop
    // (los formatos dobles usan grid-column: 1 / -1 en todas las resoluciones)

    // Recorrer items visibles y calcular su posición en el grid
    let currentColumn = 0; // 0 = izquierda, 1 = derecha

    for (let i = 0; i < visibleItems.length; i++) {
      const item = visibleItems[i];
      const isDouble = isFullRowItem(item);

      // Guardar la columna donde empieza este item
      const itemColumn = currentColumn;

      // Actualizar columna para el siguiente
      if (isDouble) {
        // Los dobles ocupan toda la fila, el siguiente empieza en columna 0
        currentColumn = 0;
      } else {
        // Los simples alternan
        currentColumn = currentColumn === 0 ? 1 : 0;
      }

      // Detectar huérfano: item simple en columna izquierda seguido de un doble
      if (!isDouble && itemColumn === 0 && isFullRowItem(visibleItems[i + 1])) {
        // Este item queda solo antes de un doble → huérfano
        orphanItems.add(item);
      }
    }

    // También detectar huérfanos causados por productos OOS
    // Recalcular considerando TODOS los items (incluyendo OOS)
    currentColumn = 0;
    const allItemsPositions = new Map();

    allItems.forEach((item) => {
      const isDouble = isFullRowItem(item);
      const isOOS = item.classList.contains('product-out-of-stock');

      allItemsPositions.set(item, { column: currentColumn, isDouble, isOOS });

      if (isDouble) {
        currentColumn = 0;
      } else {
        currentColumn = currentColumn === 0 ? 1 : 0;
      }
    });

    // Buscar casos donde un visible queda solo por OOS adyacente(s) + doble siguiente
    // Soporta múltiples OOS consecutivos entre el simple y el doble
    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i];
      const pos = allItemsPositions.get(item);

      // Saltar si es OOS o doble
      if (pos.isOOS || pos.isDouble) continue;

      // Buscar el siguiente item VISIBLE (saltando todos los OOS)
      let nextVisibleIndex = i + 1;
      while (nextVisibleIndex < allItems.length) {
        const nextItem = allItems[nextVisibleIndex];
        const nextPos = allItemsPositions.get(nextItem);
        if (!nextPos.isOOS) break; // Encontramos un visible
        nextVisibleIndex++;
      }

      const nextVisibleItem = allItems[nextVisibleIndex];
      if (!nextVisibleItem) continue;

      const nextVisiblePos = allItemsPositions.get(nextVisibleItem);

      // Contar cuántos OOS hay entre este item y el siguiente visible
      const oosCount = nextVisibleIndex - i - 1;

      // Si el siguiente visible es doble, verificar si este item queda huérfano
      if (nextVisiblePos.isDouble) {
        if (pos.column === 0) {
          // Simple en columna izquierda con OOS después
          if (oosCount > 0) {
            orphanItems.add(item);
          }
        } else if (pos.column === 1) {
          // Simple en columna derecha con OOS a la izquierda
          const prevItem = allItems[i - 1];
          if (prevItem) {
            const prevPos = allItemsPositions.get(prevItem);
            if (prevPos.isOOS) {
              orphanItems.add(item);
            }
          }
        }
      }
    }

    // Caso 4: Último producto visible solo en su fila cuando no hay más productos por cargar
    // Solo cuando el formato es vertical_actual (llegamos tras paginaciones/infinite scroll)
    const hasMoreProducts = this.hasMoreProductsToLoad();

    if (!hasMoreProducts && visibleItems.length > 0) {
      const lastVisibleItem = visibleItems[visibleItems.length - 1];
      const lastFormat = getOriginalFormat(lastVisibleItem);

      // Solo si el último es vertical_actual en columna izquierda (item simple, no doble)
      if (!isFullRowItem(lastVisibleItem) && lastFormat === 'vertical_actual') {
        // Calcular en qué columna termina el último item visible
        // Los formatos dobles resetean a columna 0 (tanto en móvil como en desktop)
        let finalColumn = 0;

        for (let i = 0; i < visibleItems.length - 1; i++) {
          if (isFullRowItem(visibleItems[i])) {
            // Los dobles ocupan toda la fila, el siguiente empieza en columna 0
            finalColumn = 0;
          } else {
            finalColumn = finalColumn === 0 ? 1 : 0;
          }
        }

        // Si el último item está en columna 0 (izquierda), está solo
        if (finalColumn === 0) {
          orphanItems.add(lastVisibleItem);
        }
      }
    }

    return orphanItems;
  }

  /**
   * Loader visible (inline o por CSS). No usar solo style.display: sin inline,
   * el tema oculta .infinite-scroll-loading vía CSS y '' !== 'none' daba true a falsas.
   */
  isLoadingIndicatorVisible(loadingIndicator) {
    if (!loadingIndicator) return false;
    return window.getComputedStyle(loadingIndicator).display !== 'none';
  }

  /**
   * Verifica si hay más productos por cargar (paginación o infinite scroll)
   */
  hasMoreProductsToLoad() {
    const paginationWrapper = document.querySelector('.wt-pagination-wrapper');
    const loadingIndicator = document.querySelector('.infinite-scroll-loading');
    const grid = document.querySelector('#product-grid');

    // Determinar si infinite scroll está activo (paginación oculta)
    const paginationHidden = paginationWrapper && paginationWrapper.style.display === 'none';

    // CASO 1: Infinite scroll activo (paginación oculta)
    if (paginationHidden) {
      // El infinite scroll expone hasMorePages en el grid tras cada carga/paginación
      const hasMoreAttr = grid?.dataset.infiniteScrollHasMorePages;
      if (hasMoreAttr === 'false') return false;
      if (hasMoreAttr === 'true') return true;

      // Fallback: productos precargados, loading, o enlace "siguiente" en paginación oculta
      const preloadedHidden = document.querySelectorAll('.collection__grid__item.preloaded-hidden');
      if (preloadedHidden.length > 0 || this.isLoadingIndicatorVisible(loadingIndicator)) return true;
      if (paginationWrapper?.querySelector('.wt-pagination__item--prev, a[rel="next"]')) return true;

      return false;
    }

    // CASO 2: Paginación tradicional visible
    if (paginationWrapper) {
      const nextLink = paginationWrapper.querySelector('.wt-pagination__item--prev');
      if (nextLink) return true;
    }

    // Fallback: solo si el loader está realmente visible (p. ej. cargando más)
    if (this.isLoadingIndicatorVisible(loadingIndicator)) return true;

    return false;
  }

  observeGridChanges() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;
    
    // Desconectar observer anterior si existe
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }
    
    this.gridObserver = new MutationObserver((mutations) => {
      // Una sola vez por lote de mutaciones, aunque el toggle de origen haga
      // classList.remove() + classList.add() por separado (2 mutation records) — evita
      // forzar el relayout de abajo dos veces por un mismo click.
      let relayoutForced = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-columns') {
          // Usar setTimeout para asegurar que el cambio se haya aplicado completamente
          setTimeout(() => {
            this.applyProductFormats();
          }, 50);
        }
        // Cambio de vista-1/2/4 (selector de columnas): switch-column.js y el script inline
        // de esta sección mutan la clase directamente en 3 sitios distintos, ninguno dispara
        // un evento propio. Este observer ya escuchaba todos los atributos de este mismo
        // nodo, así que basta con reaccionar también a "class" para avisar de forma
        // centralizada (evita depender de tocar los 3 sitios si se añade un cuarto algún día).
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Si la clase anterior no tenía ya un vista-*, esta mutación es la primera
          // asignación de vista al cargar la página (initGridToggle(), vía DOMContentLoaded),
          // no un toggle real — no hay ningún alto cacheado de una vista previa que
          // invalidar todavía. Sin este guard, el relayout de abajo se disparaba SIEMPRE
          // en la carga inicial, forzando un reflow completo de toda la parrilla (incluidas
          // las cards fuera de viewport) justo en el camino crítico de renderizado, compitiendo
          // con el propio LCP.
          const hadVista = mutation.oldValue && mutation.oldValue.includes('vista-');
          if (hadVista && !relayoutForced) {
            relayoutForced = true;
            // Cambiar de vista cambia el ancho de cada card (1↔2↔4 columnas) y, por el
            // aspect-ratio fijo de las fotos, también su alto real. Pero el alto
            // "recordado" que usa content-visibility:auto (ver scss/pages/_collection.scss)
            // para las cards aún no vistas sigue siendo el de la vista anterior hasta que
            // se relayoutean de verdad — algo que solo pasa al hacerlas visibles (con
            // scroll), dejando huecos/alturas erróneas hasta entonces. Forzamos ese
            // relayout una vez aquí (clase que desactiva content-visibility un instante,
            // leer una métrica de layout para forzar el reflow, revertir) en vez de
            // esperar a que el usuario lo descubra haciendo scroll. Desconectar/reconectar
            // el propio observer alrededor: el toggle de la clase de abajo es en sí mismo
            // una mutación de "class" sobre este mismo nodo, y sin esto se retriggerearía
            // a sí mismo sin parar.
            this.gridObserver.disconnect();
            grid.classList.add('content-viz-relayout');
            void grid.offsetHeight;
            grid.classList.remove('content-viz-relayout');
            this.gridObserver.observe(grid, { attributes: true, attributeOldValue: true });
          }

          document.dispatchEvent(new CustomEvent('collection:viewToggled'));
        }
      });
    });

    this.gridObserver.observe(grid, { attributes: true, attributeOldValue: true });
  }

  applyProductFormats() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    // Colocar los banners posicionados ANTES de leer los items, para que entren en el
    // cálculo de formatos/huérfanos en esta misma pasada. Es idempotente (omite los ya
    // colocados), así que es seguro reejecutarlo en cada evento que dispara este método
    // (carga inicial, infiniteScroll:loaded, facets:change, resize, cambio de columnas).
    this.placeBanners(grid);

    // Los productos duplicados por banner (placeBanners) llevan su propia galería
    // alternativa anidada dentro de OTRO <template> (el de posicionamiento de banners) —
    // al clonarse e insertarse aquí arriba, llega igual de inerte. Este mismo punto sirve
    // de "resync" general: cubre tanto esos banners recién colocados como el grid entero
    // reemplazado tras un filtrado (facets:change sustituye #product-grid por HTML nuevo
    // con templates sin instanciar).
    if (window.altGalleryHelpers) window.altGalleryHelpers.instantiateIn(grid);

    const columns = grid.getAttribute('data-columns');

    // Obtener todos los items (incluyendo out-of-stock para detectar adyacencias)
    const allItems = Array.from(grid.querySelectorAll('.collection__grid__item'));

    // Crear un mapa de índices: posición visible -> item
    const visibleItems = [];
    const outOfStockPositions = new Map(); // Guardar posición original de out-of-stock

    allItems.forEach((item, originalIndex) => {
      if (item.classList.contains('product-out-of-stock')) {
        outOfStockPositions.set(originalIndex, item);
      } else {
        visibleItems.push(item);
      }
    });

    // Guardar formato original si no existe (para poder recalcular).
    // Los looks quedan fuera: su ocupación la resuelve el CSS con data-look-format
    // y no deben entrar en el juego de data-format (que se reescribe al cambiar
    // de vista).
    allItems.forEach(item => {
      if (item.classList.contains('collection-look-item')) return;
      if (!item.dataset.originalFormat) {
        item.dataset.originalFormat = item.getAttribute('data-format') || 'vertical_actual';
      }
    });

    // Limpiar clases de huérfano anteriores
    allItems.forEach(item => {
      item.classList.remove('collection__grid__item--orphan');
    });

    // Solo aplicar en vista de 2 columnas
    if (columns !== '2') {
      // Limpiar todos los estilos inline en otras vistas
      allItems.forEach(item => {
        this.clearInlineStyles(item);
        // Restaurar formato original
        if (item.dataset.originalFormat) {
          item.setAttribute('data-format', item.dataset.originalFormat);
        }
      });
      return;
    }

    const isDesktop = window.innerWidth > 768;

    // Detectar items huérfanos
    // - En desktop: todos los casos (OOS, dobles, último solo)
    // - En móvil: solo el último producto solo (los demás se recolocan automáticamente)
    const orphanItems = this.detectOrphanItems(allItems, visibleItems, isDesktop);

    // Aplicar clase de huérfano (centra sin cambiar tamaño)
    orphanItems.forEach(item => {
      item.classList.add('collection__grid__item--orphan');
      const title = item.querySelector('.brn-card__title')?.textContent?.trim() || 'Sin título';
      console.log('[GridLayout] Huérfano detectado:', title);
    });

    // Aplicar formatos según data-format (solo en desktop)
    if (isDesktop) {
      visibleItems.forEach((item) => {
        const format = item.dataset.originalFormat || item.getAttribute('data-format');

        // Si ya es huérfano, el CSS de --orphan tiene prioridad, no aplicar vertical_grande
        if (item.classList.contains('collection__grid__item--orphan')) {
          this.clearInlineStyles(item);
          return;
        }

        // Aplicar formato según data-format configurado en backoffice
        switch (format) {
          case 'vertical_grande':
            this.applyVerticalGrandeFormat(item);
            break;
          case 'panoramica_actual':
            this.applyPanoramicaActualFormat(item);
            break;
          case 'panoramica_grande':
            this.applyPanoramicaGrandeFormat(item);
            break;
          case 'vertical_actual':
          default:
            this.clearInlineStyles(item);
            break;
        }
      });
    }
  }

  /**
   * Coloca los banners posicionados (collection.metafields.custom.banner) en el grid.
   *
   * Por qué aquí y no en Liquid: `banner.posicion` es un índice ABSOLUTO de slot visible
   * (la N-ésima card visible, contando productos visibles + banners). Dentro de la página N
   * de la paginación, Liquid no conoce el recuento real de slots de las páginas anteriores
   * (banners insertados antes + productos agotados ocultos), así que la antigua estimación
   * `(página-1)*page_size` se desviaba y rompía el orden, acumulándose con el infinite scroll.
   * Los banners se emiten UNA vez como <template> en #collection-banner-data y se colocan
   * aquí sobre el DOM ya concatenado, contando los slots visibles reales.
   *
   * Idempotente: un banner ya colocado existe como
   * `.collection-banner-item[data-banner-position]` en el grid, así que se omite. Al filtrar,
   * facets reemplaza el grid y #collection-banner-data, y se recolocan en la siguiente pasada.
   */
  placeBanners(grid) {
    const data = document.getElementById('collection-banner-data');
    if (!data) return;

    // Banners ocultos en este market (`hide_market`): Liquid no los emite, pero deja una
    // marca `.brn-banner-skipped` con su `posicion`. Las posiciones son slots ABSOLUTOS y
    // se numeraron contando también esos banners, así que hay que descontarlos: si no,
    // cada banner/look posterior caería un slot más abajo justo en los markets donde el
    // banner está oculto. El banner oculto no debe contar para nada.
    const skippedPositions = [];
    data.querySelectorAll('.brn-banner-skipped').forEach((el) => {
      const pos = parseInt(el.getAttribute('data-posicion'), 10);
      if (Number.isInteger(pos) && pos >= 1) skippedPositions.push(pos);
    });
    // Estrictamente menor: un look que comparte `posicion` con el banner oculto pasa a
    // ocupar el slot de este, no el anterior.
    const shiftFor = (pos) => skippedPositions.reduce((n, p) => (p < pos ? n + 1 : n), 0);

    // Banners (custom.banner) y looks (custom.shop_the_look) comparten el mismo
    // espacio de posiciones, así que se ordenan juntos. Si ambos apuntan al mismo
    // slot, el banner va primero y el look justo detrás: al empatar se le suma 1
    // a la posición efectiva del segundo (misma regla que el editor de
    // colecciones de brownie-tools).
    const seen = new Set();
    const all = [];
    data.querySelectorAll('template.brn-banner-tpl, template.brn-look-tpl').forEach((tpl) => {
      const pos = parseInt(tpl.getAttribute('data-posicion'), 10);
      if (!Number.isInteger(pos) || pos < 1) return;
      const isLook = tpl.classList.contains('brn-look-tpl');
      // Deduplicado por tipo + posicion (el primero gana). Por tipo, para que un
      // banner no descarte al look que comparte posicion con él.
      const key = `${isLook ? 'look' : 'banner'}:${pos}`;
      if (seen.has(key)) return;
      seen.add(key);
      all.push({ pos, isLook, tpl });
    });
    if (all.length === 0) return;

    all.sort((a, b) => a.pos - b.pos || (a.isLook === b.isLook ? 0 : a.isLook ? 1 : -1));

    // El desplazamiento por empate se calcula sobre TODAS las plantillas, no solo
    // sobre las pendientes: así al reejecutar (infinite scroll, filtros) el look
    // vuelve a resolver la misma posición efectiva aunque su banner ya esté puesto.
    let prevPos = null;
    let offset = 0;
    const pending = [];
    all.forEach((entry) => {
      offset = entry.pos === prevPos ? offset + 1 : 0;
      prevPos = entry.pos;

      const placed = entry.isLook
        ? `.collection-look-item[data-look-position="${entry.pos}"]`
        : `.collection-banner-item[data-banner-position="${entry.pos}"]`;
      if (grid.querySelector(placed)) return;

      const node = entry.tpl.content.firstElementChild;
      // `entry.pos` sigue siendo la posición cruda (la que lleva data-banner-position y con
      // la que se comprueba si ya está colocado); solo el slot de inserción se desplaza.
      const pos = Math.max(1, entry.pos + offset - shiftFor(entry.pos));
      if (node) pending.push({ pos, node: node.cloneNode(true) });
    });

    // Se inserta en orden de slot efectivo (tras descontar los banners ocultos).
    pending.sort((a, b) => a.pos - b.pos);
    pending.forEach((item) => this.insertBannerAt(grid, item.pos, item.node));
  }

  /**
   * Inserta un banner como el slot visible `posicion` (1-based).
   * Reglas de slot (idénticas al render original): un banner SIEMPRE cuenta como slot; un
   * producto solo cuenta si está visible (sin product-out-of-stock); los productos precargados
   * aún ocultos (preloaded-hidden) no cuentan y frenan la colocación hasta que se revelen
   * (se reintenta en el siguiente infiniteScroll:loaded).
   * @returns {boolean} true si se insertó
   */
  insertBannerAt(grid, posicion, node) {
    const items = Array.from(grid.children).filter(
      (el) => el.classList && el.classList.contains('collection__grid__item')
    );

    let slots = 0;
    for (const item of items) {
      // Punto de inserción: hay posicion-1 slots antes de este item.
      if (slots === posicion - 1) {
        grid.insertBefore(node, item);
        return true;
      }
      // Bloque de productos precargados aún ocultos: no colocar más allá de lo revelado.
      if (item.classList.contains('preloaded-hidden')) return false;
      if (
        item.classList.contains('collection-banner-item') ||
        !item.classList.contains('product-out-of-stock')
      ) {
        slots++;
      }
    }

    // Fin del contenido revelado, sin productos precargados pendientes, y la posición es
    // justo el siguiente slot tras el último visible: es el slot correcto, se anexa al final.
    // Si luego llegan más productos (scroll infinito), se añaden después del banner igualmente.
    if (slots === posicion - 1) {
      grid.appendChild(node);
      return true;
    }
    // Posición más allá del último slot real (p. ej. la posición contaba productos que la
    // web oculta por agotados, o la colección perdió productos después de crear el banner).
    // Si ya no quedan páginas por cargar, se clava al final en vez de no mostrarse nunca.
    if (slots < posicion - 1 && !this.hasMoreProductsToLoad()) {
      grid.appendChild(node);
      return true;
    }
    return false;
  }

  clearInlineStyles(item) {
    item.removeAttribute('style');
    
    const brn_card = item.querySelector('.brn-card');
    const card_container = item.querySelector('.card__container');
    
    if (brn_card) {
      brn_card.removeAttribute('style');
    }
    
    if (card_container) {
      card_container.removeAttribute('style');
    }
  }

  applyVerticalGrandeFormat(item) {
    // Cambiar el data-format para que el CSS lo maneje
    item.setAttribute('data-format', 'vertical_grande');
    this.clearInlineStyles(item);
  }

  applyPanoramicaActualFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_actual"
    this.clearInlineStyles(item);
  }

  applyPanoramicaGrandeFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_grande"
    this.clearInlineStyles(item);
  }
}

// Inicializar
new CollectionGridLayout();

